<!--
  Ask ELSA — order analysis (AB#21202).

  A TAB, not an overlay and not a button. The order detail is already the
  right-hand pane, so a panel sliding in from the right would cover the
  timeline and the comments a CSM needs open while reading a risk claim — and
  every claim here cites one of them. A button in the action row would be a
  second control for one destination, which is what AB#21204 just finished
  removing twelve of.

  Two cards, and they are not interchangeable:

    - "Time in current stage" is MEASURED. Two timestamps and a percentile over
      past orders. It renders when ELSA is unavailable, unkeyed or wrong, and it
      is what the card below can be checked against.
    - "ELSA's read" is GENERATED and says so. Every claim carries the event it
      came from, because an unverifiable claim is indistinguishable on screen
      from a verifiable one and the first wrong one costs the feature its
      audience.

  There is no confidence percentage. See useOrderAnalysis and AB#21202's
  comments for why coverage replaced it.
-->
<template>
  <div class="ask-elsa">
    <!-- MEASURED -->
    <section class="elsa-card" data-test="elsa-measured">
      <header class="elsa-card-head">
        <h3 class="elsa-card-title">Time in current stage</h3>
        <span class="elsa-card-note">measured · from this order's timeline</span>
      </header>

      <div v-if="isLoading && !measured" class="elsa-skeleton" aria-hidden="true"></div>

      <template v-else-if="measured">
        <p class="elsa-metric">
          <span
            class="elsa-metric-value"
            :class="`elsa-metric-value--${measured.verdict ?? 'none'}`"
          >
            {{ formatDaysHours(measured.days_in_stage) }}
          </span>
          <span class="elsa-metric-unit">
            {{ measured.terminal ? 'since' : 'in' }} {{ measured.stage_label }}
            <!--
              The gap, in the headline, so the band confirms a number that has
              already been read rather than being the only place it exists.
            -->
            <template v-if="gapText">· {{ gapText }}</template>
          </span>
        </p>

        <div v-if="measured.has_comparison" class="elsa-gauge">
          <!--
            The value flag gets a row to itself. It used to sit in the same
            strip as the zone names, and at the widths this pane actually takes
            the two collided.
          -->
          <div class="elsa-gauge-flagrow">
            <span class="elsa-gauge-flag" :style="{ left: markerLeft }">
              {{ formatDaysHours(measured.days_in_stage) }}
            </span>
          </div>

          <!--
            One linear scale, and the segments really are on it.

            These carried flex-grow without a flex-basis, so each one first
            claimed the width of its own label and only the LEFTOVER was shared
            by the grow factors. Every boundary sat to the right of the tick
            that named it, while the marker was placed on the true scale — so a
            reading genuinely inside Watch was drawn on the edge of Typical.
            flex-basis: 0 is the fix; the labels moved to the key below, which
            is what made the bug possible in the first place.
          -->
          <div class="elsa-gauge-rail" role="img" :aria-label="bandDescription">
            <div
              class="elsa-gauge-zone elsa-gauge-zone--good"
              :style="{ flexGrow: segments.good }"
            ></div>
            <div
              class="elsa-gauge-zone elsa-gauge-zone--watch"
              :class="{ 'elsa-gauge-zone--on': measured.verdict === 'watch' }"
              :style="{ flexGrow: segments.watch }"
            ></div>
            <div
              class="elsa-gauge-zone elsa-gauge-zone--late"
              :class="{ 'elsa-gauge-zone--on': measured.verdict === 'late' }"
              :style="{ flexGrow: segments.late }"
            ></div>

            <!--
              The promise on the same axis. Only drawn when the backend could
              place it: a mark past the right-hand edge piles up against the end
              and reads as "due now", which is the opposite of a distant date.
            -->
            <span
              v-if="promiseLeft"
              class="elsa-gauge-promise"
              :style="{ left: promiseLeft }"
              data-test="elsa-promise-mark"
            ></span>
            <span class="elsa-gauge-needle" :style="{ left: markerLeft }"></span>
          </div>

          <div class="elsa-gauge-ticks">
            <span style="left: 0">0</span>
            <span :style="{ left: tickLeft(measured.typical_high) }"
              >{{ formatDaysHours(measured.typical_high) }}</span
            >
            <span :style="{ left: tickLeft(measured.late_from) }">{{ formatDaysHours(measured.late_from) }}</span>
            <span style="left: 100%">{{ formatDaysHours(measured.scale_max) }}+</span>
          </div>

          <!--
            The zone names live here rather than inside the bar. Colour alone
            cannot carry a three-way distinction for a reader who does not see
            it, and a word inside a segment distorts the segment.
          -->
          <div class="elsa-gauge-key">
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'good' }">
              <i class="elsa-sw elsa-sw--good"></i>Typical
            </span>
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'watch' }">
              <i class="elsa-sw elsa-sw--watch"></i>Watch{{
                measured.verdict === 'watch' ? ' — you are here' : ''
              }}
            </span>
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'late' }">
              <i class="elsa-sw elsa-sw--late"></i>Late{{
                measured.verdict === 'late' ? ' — you are here' : ''
              }}
            </span>
            <span v-if="promiseLeft" class="elsa-gauge-key--promise">
              <i class="elsa-sw elsa-sw--promise"></i>Promised ship {{ promiseDateText }}
            </span>
          </div>

          <p class="elsa-band-why">
            {{ measured.stage_label }} has taken
            <strong>{{ formatDaysHoursRange(measured.typical_low, measured.typical_high) }}</strong>
            across the last {{ measured.sample_size }} orders at this factory.
            <template v-if="promiseText">
              This one is at <strong>{{ formatDaysHours(measured.days_in_stage) }}</strong
              >, with the promised ship date <strong>{{ promiseText }}</strong
              >.
            </template>
          </p>
        </div>

        <!--
          A finished order (completed/cancelled) is not waiting in a stage, so
          the elapsed time is not a delay and there is nothing to compare it to.
          Said plainly, so the absence of a band reads as intended rather than as
          missing data.
        -->
        <p v-else-if="measured.terminal" class="elsa-band-why elsa-band-why--thin" data-test="elsa-terminal">
          This order is finished ({{ measured.stage_label }}). The time above is how long since
          it finished, not a delay — so there's nothing to compare.
        </p>

        <!--
          No band rather than a band over four orders. A percentile that swings
          on one outlier teaches people the measurement is noise, which would
          cost more than the missing comparison.
        -->
        <p v-else class="elsa-band-why elsa-band-why--thin" data-test="elsa-no-band">
          Not enough history at this factory to compare this stage yet
          <template v-if="measured.sample_size > 0">
            ({{ measured.sample_size }} comparable
            {{ measured.sample_size === 1 ? 'order' : 'orders' }})</template
          >.
        </p>
      </template>

      <!--
        The card could not be filled. Without this the header renders over
        nothing and the card reads as broken rather than as unavailable — which
        is exactly what it looked like when the tab shipped ahead of its
        endpoint. A measured card that cannot say anything has to say that.
      -->
      <p v-else class="elsa-band-why elsa-band-why--thin" data-test="elsa-measured-unavailable">
        {{ error || 'These figures could not be loaded right now.' }}
      </p>
    </section>

    <!-- GENERATED -->
    <section class="elsa-card" data-test="elsa-reading">
      <!--
        Freshness is the second thing on this card, not the last.

        It used to be an absolute date in the footer, under everything it was
        meant to qualify — by the time a reader met it they had already believed
        the summary. As an age beside the name, with the count of events behind
        it, it is read before the words it applies to. Regenerate sits on the
        same line for the same reason: the moment of doubt and the remedy are
        one glance apart, rather than a card's height and a support launcher
        away from each other.
      -->
      <header class="elsa-card-head elsa-read-head">
        <h3 class="elsa-card-title">
          <button
            type="button"
            class="elsa-collapse"
            :aria-expanded="!readCollapsed"
            :aria-label="readCollapsed ? `Expand ELSA's read` : `Collapse ELSA's read`"
            :title="readCollapsed ? 'Expand' : 'Collapse'"
            data-test="elsa-read-collapse"
            @click="toggleReadCollapsed"
          >
            <ChevronDownIcon class="elsa-collapse-icon" :class="{ 'elsa-collapse-icon--collapsed': readCollapsed }" aria-hidden="true" />
          </button>
          <AgentAvatar
            agent="elsa"
            :size="26"
            :state="isGenerating ? 'thinking' : reading && !isStale ? 'done' : 'idle'"
            class="elsa-head-face"
          />
          ELSA's read
        </h3>
        <span
          v-if="reading"
          class="elsa-freshness"
          :class="{ 'elsa-freshness--stale': isStale || reading?.refresh?.state === 'failed' }"
          data-test="elsa-freshness"
        >
          <span class="elsa-dot"></span>
          {{ freshnessText }}
        </span>
        <span v-else class="elsa-card-note">{{ readingNote }}</span>
        <!-- AB#21909 — the desk's verdict on this read. -->
        <ReadingVerdict
          v-if="reading && reading.status"
          :key="reading.generated_at ?? 'r'"
          :order-item-id="orderItemId"
          :initial="reading.my_feedback ?? null"
          source="order_page"
          size="md"
          class="elsa-read-verdict"
        />
        <button
          v-if="reading"
          type="button"
          class="elsa-btn elsa-regen"
          :disabled="isGenerating"
          data-test="elsa-regenerate"
          @click="generate"
        >
          <ElsaMark class="elsa-mark" />
          {{ isGenerating ? 'Reading…' : 'Regenerate' }}
        </button>
      </header>

      <div v-show="!readCollapsed" class="elsa-read-body" data-test="elsa-read-body">
      <!-- Unavailable: the measured card above is unaffected, and says so. -->
      <div v-if="!available" class="elsa-notice" data-test="elsa-unavailable">
        <strong>ELSA is not available right now.</strong>
        The figures above are unaffected — they come from this order's own timeline, not from a
        model.
      </div>

      <div v-else-if="isGenerating" class="elsa-empty">
        <p>Reading this order…</p>
      </div>

      <!--
        Could not even load. Distinct from "never run": offering Generate here
        would be offering a button that cannot work, because the request it
        makes is the one that just failed. Say what happened and stop.
      -->
      <!--
        `error` is never null here — loadFailed requires it — so the message is
        the whole of what this card says. It used to carry a bold "This order's
        analysis could not be loaded." above it, which read as a heading over the
        only informative line: with the server now answering "This order is not
        available to you", the pair said the same thing twice and the reader's eye
        stopped at the half that says nothing. useOrderAnalysis already supplies a
        generic sentence for the cases with no server text, so nothing is lost.
      -->
      <div v-else-if="loadFailed" class="elsa-notice" data-test="elsa-load-failed">
        {{ error }}
      </div>

      <!-- Never run for this order. -->
      <div v-else-if="!reading" class="elsa-empty" data-test="elsa-not-run">
        <p>
          ELSA reads this order's timeline, its order communication and its internal comments, and says
          whether it is on track — with the event behind every claim.
        </p>
        <button type="button" class="elsa-btn elsa-btn--primary" @click="generate">
          <ElsaMark class="elsa-mark" />
          Generate analysis
        </button>
        <p v-if="error" class="elsa-error" role="status">{{ error }}</p>
      </div>

      <template v-else>
        <!--
          The verdict as a statement, not a chip.

          A small coloured pill above a paragraph makes the reader do the work
          of joining them. The colour now runs down the left edge of the words
          it qualifies, and the words are set large enough to be the thing that
          gets read.
        -->
        <div class="elsa-verdict" :class="`elsa-verdict--${reading.status}`">
          <p class="elsa-verdict-kicker">{{ statusLabel }}</p>
          <!-- The one line a busy person reads; the detail waits behind "Read more". -->
          <p class="elsa-verdict-text" data-test="elsa-headline">{{ headlineText }}</p>
          <p v-if="detailsOpen && summaryAdds" class="elsa-verdict-detail" data-test="elsa-summary">
            {{ reading.summary }}
          </p>
          <p v-if="etaText" class="elsa-eta">
            Ships <strong>{{ etaText }}</strong>
            <span v-if="reading.eta.basis" class="elsa-eta-basis"> — {{ reading.eta.basis }}</span>
          </p>
        </div>

        <!--
          "Do this next", promoted out of grey small print.

          It was the smallest text on the card and ended in a full stop: a
          recommendation that then asked the reader to go and find the right
          button themselves. The buttons open the same modals the action row
          opens, and each is offered only where that row would have offered it.
        -->
        <div v-if="nextAction" class="elsa-cta" data-test="elsa-cta">
          <div class="elsa-cta-body">
            <p class="elsa-cta-kicker">Do this next</p>
            <p class="elsa-cta-text">{{ nextAction.claim }}</p>
            <p v-if="nextAction.evidence" class="elsa-cta-why">{{ nextAction.evidence }}</p>
          </div>
          <!--
            AB#21732 — the buttons come from the action's own code: "Set the
            in-hand date" opens the In Hand drawer with the date filled in,
            not a comment box. An internal note stays as the alternative.
          -->
          <div v-if="ctaButtons.length" class="elsa-cta-btns">
            <button
              v-for="(b, i) in ctaButtons"
              :key="b.code"
              type="button"
              class="elsa-btn"
              :class="{ 'elsa-btn--primary': i === 0 }"
              :data-test="`elsa-cta-${b.code}`"
              @click="act(b.code, nextAction?.claim, b.date)"
            >
              {{ b.label }}
            </button>
          </div>
        </div>

        <button
          v-if="hasMore"
          type="button"
          class="elsa-more"
          :aria-expanded="detailsOpen"
          data-test="elsa-more"
          @click="detailsOpen = !detailsOpen"
        >
          <ChevronDownIcon class="elsa-more-ic" :class="{ 'elsa-more-ic--open': detailsOpen }" aria-hidden="true" />
          <span>{{ detailsOpen ? 'Show less' : 'Read more' }}</span>
          <span v-if="!detailsOpen && moreHint" class="elsa-more-hint">· {{ moreHint }}</span>
        </button>

        <template v-if="detailsOpen">
        <ElsaClaimGroup title="What is going wrong" kind="risk" :claims="reading.risks" />
        <!-- AB#21732 — signals include watch items, so not "What is going right". -->
        <ElsaClaimGroup title="Worth knowing" kind="good" :claims="reading.signals" />
        <!--
          The remaining actions only. The first is already above as the CTA, and
          repeating it verbatim two inches lower reads as two recommendations.
        -->
        <ElsaClaimGroup
          v-if="remainingActions.length"
          title="Also worth doing"
          kind="action"
          :claims="remainingActions"
          :action-button="rowButton"
          @act="(code, claim, date) => act(code, claim, date)"
        />

        <!--
          Coverage, not confidence. What the reading was actually based on is a
          fact; how sure the model felt is not.
        -->
        <div v-if="coverageChips.length" class="elsa-coverage">
          <span class="elsa-coverage-label">Read from</span>
          <span v-for="chip in coverageChips" :key="chip.label" class="elsa-chip">
            <strong>{{ chip.count }}</strong> {{ chip.label }}
          </span>
        </div>
        </template>

        <p v-if="error" class="elsa-error" role="status">{{ error }}</p>
      </template>
      </div>
    </section>

    <!--
      CONVERSATION (AB#21422). Keyed by the same order item as the tab, so a
      new order is a new thread. Its draft buttons use the same `act` wire as
      the read card's "do this next" buttons, with the draft's text along.
    -->
    <TalkToElsaCard
      :order-item-id="orderItemId"
      :order-no="orderNo ?? ''"
      @act="(action, text) => emit('act', action, text)"
    />
  </div>
</template>

<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { computed, onMounted, ref, watch, type ComputedRef } from 'vue';

import ElsaClaimGroup from '@/components/Dashboard/Order/components/ElsaClaimGroup.vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import ElsaMark from '@/components/Dashboard/Order/components/ElsaMark.vue';
import ReadingVerdict from '@/components/Shared/ReadingVerdict.vue';
import { isCardCollapsed, setCardCollapsed } from '@/utils/elsaCardPrefs';
import { formatDaysHours, formatDaysHoursRange } from '@/utils/formatDuration';
import TalkToElsaCard from '@/components/Dashboard/Order/components/TalkToElsaCard.vue';
import { can, formatDate } from '@/composables';
import { useOrderAnalysis, type Claim } from '@/composables/useOrderAnalysis';
import { buttonsFor, type ActionAbilities, type ResolvedButton } from '@/utils/elsaActions';
import { firstSentence, sameText } from '@/utils/elsaAnswer';

const props = defineProps<{
  orderItemId: number;
  orderNo?: string | number;
  /** AB#21732 — the order has Zendesk threads (HummelUS), so reply buttons can open them. */
  hasSupportThreads?: boolean;
}>();

/**
 * `staleness` drives the dot on the tab, which this component cannot reach.
 * `act` asks the page to open the modal a recommendation is asking for, rather
 * than telling the reader to go and find the button.
 */
const emit = defineEmits<{
  (e: 'staleness', stale: boolean): void;
  /** AB#21732 — an action code (elsaActions.ts), the claim, and its date. */
  (e: 'act', action: string, text?: string, date?: string | null): void;
}>();

// One order per mount. OrderDetail keys this component on the order item, so
// clicking to another order remounts rather than mutating — the one thing this
// tab must never do is show the previous order's reading under the current
// order's number, and a key is a far harder guarantee of that than a watcher.
const analysis = useOrderAnalysis(props.orderItemId);

const measured = computed(() => analysis.measured.value);
const reading = computed(() => analysis.reading.value);
const available = computed(() => analysis.available.value);
const isLoading = computed(() => analysis.isLoading.value);
const isGenerating = computed(() => analysis.isGenerating.value);
const error = computed(() => analysis.error.value);
const isStale = computed(() => analysis.isStale.value);

/**
 * The load itself failed, as opposed to the order simply never having been
 * analysed. The two look identical in state — no reading — and must not look
 * identical on screen: one invites a click, the other has to explain itself.
 */
const loadFailed = computed(
  () => !isLoading.value && measured.value === null && error.value !== null
);

const generate = (): Promise<void> => analysis.generate();

// Collapse state for the read card, remembered per user. Open by default.
const storage = typeof window !== 'undefined' ? window.localStorage : null;
const readCollapsed = ref(isCardCollapsed(storage, 'read'));
const toggleReadCollapsed = (): void => {
  readCollapsed.value = !readCollapsed.value;
  setCardCollapsed(storage, 'read', readCollapsed.value);
};

onMounted(() => analysis.load());

// Report staleness upward whenever it changes, including on the first load —
// the tab's dot has no other source for it.
watch(isStale, (stale) => emit('staleness', stale), { immediate: true });

const STATUS_LABELS: Record<string, string> = {
  green: 'On track',
  orange: 'At risk',
  red: 'Delayed',
};

const statusLabel: ComputedRef<string> = computed(
  () => STATUS_LABELS[reading.value?.status ?? ''] ?? 'Unknown'
);

const readingNote: ComputedRef<string> = computed(() => {
  if (!available.value) return 'unavailable';
  if (!reading.value) return 'not run for this order';

  return 'written by ELSA · check before quoting';
});

/**
 * Band geometry. One linear scale from 0 to scale_max carries the segment
 * widths, the marker and the ticks, so a tick always lines up with the segment
 * edge it names — a legend spread evenly across a band whose segments are not
 * even is a chart that lies.
 */
const scaleMax: ComputedRef<number> = computed(() => Math.max(1, measured.value?.scale_max ?? 1));

const segments = computed(() => {
  const high = measured.value?.typical_high ?? 0;
  const late = measured.value?.late_from ?? 0;

  return {
    good: high,
    watch: Math.max(0, late - high),
    late: Math.max(0, scaleMax.value - late),
  };
});

/**
 * A position on the scale, as a percentage.
 *
 * Rounded to four places: 11/20 lands on 55.00000000000001 in binary floating
 * point, and shipping that into a style attribute is both noise in the DOM and
 * a value no assertion can match without knowing about float error. Four places
 * is finer than a pixel on any width this pane takes.
 */
const pct = (value: number): string => {
  const raw = Math.min(100, Math.max(0, (value / scaleMax.value) * 100));

  return `${Math.round(raw * 10000) / 10000}%`;
};

const markerLeft: ComputedRef<string> = computed(() => pct(measured.value?.days_in_stage ?? 0));

const tickLeft = (value: number | null): string => pct(value ?? 0);

/**
 * Where the promised ship date falls on the same axis, or '' for no mark.
 *
 * The backend withholds promise_at_days when the promise sits past the drawn
 * scale, so an empty string here means "do not draw", not "no promise" — the
 * date itself can still be present and still be worth saying in prose.
 */
const promiseLeft: ComputedRef<string> = computed(() => {
  const at = measured.value?.promise_at_days;

  return typeof at === 'number' ? pct(at) : '';
});

/**
 * The promised date, short.
 *
 * Deliberately not formatDate: this sits in a key beneath a chart, next to
 * three other entries, and "15th September 2026" there pushes the key onto a
 * second line and swamps the zone names it is meant to sit alongside. The year
 * is redundant on an order in production, and the long form is still used in
 * the sentence below the chart where there is room for it.
 */
const promiseDateText: ComputedRef<string> = computed(() => {
  const iso = measured.value?.promised_ship_date;
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
});

/**
 * The promise in the reader's terms — "2 days away", "3 days ago", "today".
 *
 * Days, not a date, because the decision this drives is whether to chase the
 * factory now, and nobody makes that decision by subtracting dates in their
 * head while reading a card.
 */
const promiseText: ComputedRef<string> = computed(() => {
  const days = measured.value?.days_until_promise;
  if (typeof days !== 'number') return '';

  const whole = Math.round(Math.abs(days));

  if (whole === 0) return 'today';

  const unit = whole === 1 ? 'day' : 'days';

  return days > 0 ? `${whole} ${unit} away` : `${whole} ${unit} ago`;
});

/**
 * How far past typical this order is, for the headline.
 *
 * Only shown once it IS past typical. Below the line the number needs no
 * qualifier, and "2.5 inside typical" is a statistic nobody asked for.
 */
const gapText: ComputedRef<string> = computed(() => {
  const m = measured.value;
  if (!m?.has_comparison || m.typical_high === null) return '';

  const over = m.days_in_stage - m.typical_high;
  if (over <= 0) return '';

  return `${formatDaysHours(over)} beyond typical`;
});

const bandDescription: ComputedRef<string> = computed(() => {
  const m = measured.value;
  if (!m) return '';

  return `${formatDaysHours(m.days_in_stage)} in ${m.stage_label}; typical is ${formatDaysHours(m.typical_low)} to ${formatDaysHours(m.typical_high)}, late from ${formatDaysHours(m.late_from)}.`;
});

const etaText: ComputedRef<string> = computed(() => {
  const eta = reading.value?.eta;
  if (!eta?.earliest || !eta?.latest) return '';

  // Always a range. A single date gets pasted into a customer email and becomes
  // a commitment the business never made.
  return `${formatDate(eta.earliest)} – ${formatDate(eta.latest)}`;
});

const COVERAGE_LABELS: Record<string, [string, string]> = {
  timeline_events: ['timeline event', 'timeline events'],
  internal_comments: ['internal comment', 'internal comments'],
  // The feature is called Order Communication; the things inside it are
  // conversations, which is also what the modal's own empty state calls them.
  // "1 order communication" does not pluralise into anything a person would say.
  support_threads: ['conversation', 'conversations'],
  // AB#21728 — General Comments, internal notes and the order comment feed.
  order_notes: ['order note', 'order notes'],
};

const coverageChips = computed(() =>
  Object.entries(reading.value?.coverage ?? {}).map(([key, count]) => {
    const [one, many] = COVERAGE_LABELS[key] ?? [key, key];

    return { count, label: count === 1 ? one : many };
  })
);

/**
 * How old the reading is, in the terms that decide whether to trust it.
 *
 * An age rather than a date, because "12 September" requires the reader to know
 * today's date and to care; "2 days old" does not. The event count is appended
 * when there is one, and that is the part that actually matters — a reading
 * written an hour ago with a stage change behind it is staler than one written
 * last week on an order that has not moved.
 */
const freshnessText: ComputedRef<string> = computed(() => {
  const r = reading.value;
  if (!r) return '';

  const age = r.generated_at ? relativeAge(r.generated_at) : 'just now';

  // AB#21582 — when the reading refreshes itself, say what it is doing. A queued
  // or running refresh is the honest answer to "is this current?": not yet, but
  // it will be, without a click. A failure keeps the previous reading and says so.
  const refresh = r.refresh;
  if (refresh?.auto) {
    if (refresh.state === 'generating') return `updated ${age} · refreshing…`;
    if (refresh.state === 'queued') return `updated ${age} · refresh pending`;
    if (refresh.state === 'failed') return `updated ${age} · could not refresh, retrying`;
  }

  if (!isStale.value) {
    const why = refresh?.auto ? refreshReasonText(refresh.reason) : '';
    return why ? `updated ${age} · ${why}` : `updated ${age}`;
  }

  const events = r.events_since;

  return `${age} · ${events} ${events === 1 ? 'event' : 'events'} since`;
});

/** Why the last automatic regeneration ran, in the reader's words. Empty for a manual one. */
const refreshReasonText = (reason: string | null | undefined): string => {
  switch (reason) {
    case 'status':
      return 'after a status change';
    case 'comment':
      return 'after a comment';
    case 'activity':
      return 'after an update';
    case 'zendesk':
      return 'after a support message';
    case 'whatsapp':
      return 'after a supplier message';
    case 'sweep':
      return 'by the nightly check';
    case 'prompt-version':
      return 'with the latest model';
    default:
      return '';
  }
};

/**
 * A coarse, unambiguous age. Deliberately not a library.
 *
 * Rounds down, so a reading never claims to be newer than it is, and stops at
 * days: past a week the exact figure stops changing the decision, and "14 days
 * old" already says everything "2 weeks old" would.
 */
const relativeAge = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';

  const minutes = Math.floor((Date.now() - then) / 60000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);

  return `${days} day${days === 1 ? '' : 's'} old`;
};

/**
 * The single action the card leads with, and the rest.
 *
 * The model returns actions in priority order, so the first is the one to
 * promote. The others keep their group lower down; repeating the first in both
 * places would read as two separate recommendations that happen to match.
 */
const nextAction = computed(() => reading.value?.actions?.[0] ?? null);
const remainingActions = computed(() => (reading.value?.actions ?? []).slice(1));

// Headline first; the summary, the lists and the coverage fold behind "Read more".
const detailsOpen = ref(false);
watch(
  () => [props.orderItemId, reading.value?.generated_at],
  () => (detailsOpen.value = false)
);
const headlineText = computed(() => {
  const r = reading.value;
  if (!r) return '';
  return (r.headline ?? '').trim() || firstSentence(r.summary);
});
const summaryAdds = computed(
  () => !!reading.value?.summary && !sameText(reading.value.summary, headlineText.value)
);
const moreHint = computed(() => {
  const r = reading.value;
  if (!r) return '';
  const bits: string[] = [];
  if (r.risks.length) bits.push(`${r.risks.length} going wrong`);
  if (r.signals.length) bits.push(`${r.signals.length} worth knowing`);
  if (remainingActions.value.length) bits.push(`${remainingActions.value.length} more to do`);
  return bits.join(' · ');
});
const hasMore = computed(
  () =>
    summaryAdds.value ||
    !!reading.value?.risks.length ||
    !!reading.value?.signals.length ||
    remainingActions.value.length > 0 ||
    coverageChips.value.length > 0
);

const abilities = computed<ActionAbilities>(() => ({ can: (p: string) => Boolean(can(p)), hasSupportThreads: Boolean(props.hasSupportThreads) }));

const ctaButtons = computed<ResolvedButton[]>(() => buttonsFor(nextAction.value, abilities.value));

/** One button per "Also worth doing" row — its own control only, never a second note button. */
const rowButton = (claim: Claim): ResolvedButton | null => {
  const [first] = buttonsFor(claim, abilities.value);
  return first && first.code !== 'add_internal_note' ? first : null;
};

// A note opened from a recommendation starts with that recommendation in it.
const act = (code: string, claim?: string, date?: string | null): void =>
  emit('act', code, code === 'add_internal_note' ? claim : undefined, date ?? null);
</script>

<style scoped>
.ask-elsa {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;

  /*
   * Room for the support launcher.
   *
   * The Zendesk messaging launcher is fixed to the viewport's bottom-right on
   * every page of this portal (AB#21165) and floats above this pane. Without
   * this padding the last row of the tab — which is exactly where Regenerate
   * sits — comes to rest underneath it and cannot be clicked at all.
   *
   * Padding rather than a z-index fight: the launcher SHOULD be on top. It is
   * how someone reports the very problem they are looking at, and burying it
   * under an order pane would be the worse trade.
   *
   * The number is the launcher (~64px) plus its own viewport margin, rounded
   * up. If Zendesk's launcher is ever moved or resized in Admin Center, this is
   * the one place that needs to follow it.
   */
  padding-bottom: 92px;
}

.elsa-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 9px;
  padding: 16px 18px;
}

.elsa-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.elsa-card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
}

.elsa-card-note {
  font-size: 12px;
  color: var(--ink-soft);
}

/* Collapse toggle — a quiet chevron ahead of the card name. */
.elsa-collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  margin-right: 1px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ink-mute);
  cursor: pointer;
}

.elsa-collapse:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.elsa-collapse:focus-visible {
  outline: 2px solid #2f6f63;
  outline-offset: 1px;
}

.elsa-collapse-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.15s ease;
}

.elsa-collapse-icon--collapsed {
  transform: rotate(-90deg);
}

.elsa-read-body {
  display: block;
}

.elsa-mark {
  width: 15px;
  height: 15px;
  color: #7c5cd6;
}
.elsa-head-face {
  margin-right: 2px;
}

.elsa-skeleton {
  height: 76px;
  border-radius: 6px;
  background: var(--surface-2);
}

.elsa-metric {
  margin: 0;
}

.elsa-metric-value {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

/*
 * The headline takes the verdict's colour, so the answer is legible before the
 * eye reaches the bar at all. Ink for "typical" — a number that is fine does
 * not need a colour to say so, and green here would compete with the gauge.
 */
.elsa-metric-value--watch {
  color: var(--warning);
}

.elsa-metric-value--late {
  color: var(--danger);
}

.elsa-metric-unit {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-soft);
  margin-left: 4px;
}

.elsa-gauge {
  margin-top: 14px;
}

/*
 * The value flag, on a row of its own.
 *
 * It shared a strip with the zone names before, and at the widths this pane
 * takes on a laptop the two overlapped — the number sat on top of the word.
 */
.elsa-gauge-flagrow {
  position: relative;
  /* Tall enough to clear the needle's cap, which sits 5px proud of the rail. */
  height: 34px;
}

.elsa-gauge-flag {
  position: absolute;
  bottom: 11px;
  transform: translateX(-50%);
  background: var(--ink);
  color: var(--surface);
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.elsa-gauge-flag::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -3px;
  transform: translateX(-50%) rotate(45deg);
  width: 7px;
  height: 7px;
  background: var(--ink);
  border-radius: 1px;
}

.elsa-gauge-rail {
  position: relative;
  display: flex;
  height: 12px;
}

/*
 * flex-basis: 0 is load-bearing.
 *
 * Without it flex-grow shares only the space LEFT OVER after every item has
 * taken its content width, so the segments were drawn at the wrong widths and
 * disagreed with the ticks and the marker — which are on the true scale. That
 * is the whole reason a reading inside Watch appeared to sit on the edge of
 * Typical. min-width: 0 stops the same thing happening again by any other
 * route.
 */
.elsa-gauge-zone {
  flex-basis: 0;
  min-width: 0;
}

/*
 * Only the zone the order is actually in is saturated. Three full-strength
 * blocks make a traffic light that shouts before the number has been read; the
 * washes still delimit the scale without competing with the answer.
 */
.elsa-gauge-zone--good {
  background: #cfe8dc;
  border-radius: 4px 0 0 4px;
}

.elsa-gauge-zone--watch {
  background: #f4e2c4;
}

.elsa-gauge-zone--late {
  background: #f6d6d2;
  border-radius: 0 4px 4px 0;
}

.elsa-gauge-zone--watch.elsa-gauge-zone--on {
  background: #b0741a;
}

.elsa-gauge-zone--late.elsa-gauge-zone--on {
  background: #c4362c;
}

/*
 * A real pointer. The 8px triangle it replaces was the weakest mark on a card
 * whose entire job is to show where one number falls.
 */
.elsa-gauge-needle {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 3px;
  border-radius: 2px;
  background: var(--ink);
  transform: translateX(-50%);
  z-index: 2;
}

.elsa-gauge-needle::after {
  content: '';
  position: absolute;
  left: 50%;
  top: -5px;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--ink);
  box-shadow: 0 0 0 2.5px var(--surface);
}

/* Dashed, and in ELSA's purple rather than a status colour: the promise is a
   date, not a verdict, and painting it amber would assert one. */
.elsa-gauge-promise {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 2px;
  z-index: 1;
  transform: translateX(-50%);
  background: repeating-linear-gradient(180deg, #7c5cd6 0 3px, transparent 3px 6px);
}

.elsa-gauge-ticks {
  position: relative;
  height: 15px;
  margin-top: 10px;
  font-size: 10.5px;
  color: var(--ink-mute);
}

.elsa-gauge-ticks span {
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.elsa-gauge-ticks span:first-child {
  transform: none;
}

.elsa-gauge-ticks span:last-child {
  transform: translateX(-100%);
}

/*
 * The key. The zone names used to sit inside the bar, which is both what
 * distorted the geometry and what left the distinction resting on colour alone.
 */
.elsa-gauge-key {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 9px;
  font-size: 11.5px;
  color: var(--ink-soft);
}

.elsa-gauge-key span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.elsa-gauge-key .elsa-gauge-key--on,
.elsa-gauge-key span.elsa-gauge-key--on {
  color: var(--ink);
  font-weight: 700;
}

.elsa-gauge-key--promise {
  color: #8b6ee0;
  font-weight: 600;
}

.elsa-sw {
  width: 12px;
  height: 8px;
  border-radius: 2px;
  flex: none;
}

/*
 * A key swatch shows the colour the bar is actually painted, which means the
 * washes — and the saturated colour only on the zone the order is in. A key
 * that shows three strong colours against a bar showing one is a key for a
 * different chart.
 */
.elsa-sw--good {
  background: #cfe8dc;
}

.elsa-sw--watch {
  background: #f4e2c4;
}

.elsa-sw--late {
  background: #f6d6d2;
}

.elsa-gauge-key--on .elsa-sw--watch {
  background: #b0741a;
}

.elsa-gauge-key--on .elsa-sw--late {
  background: #c4362c;
}

.elsa-sw--promise {
  width: 2px;
  height: 12px;
  border-radius: 0;
  background: repeating-linear-gradient(180deg, #7c5cd6 0 3px, transparent 3px 6px);
}

.elsa-band-why {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--ink-soft);
}

.elsa-band-why--thin {
  margin-top: 12px;
}

.elsa-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}

/*
 * The header is now a row of three: name, freshness, Regenerate hard right.
 */
.elsa-read-head {
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.elsa-freshness {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-soft);
  background: var(--surface-2);
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}

/*
 * Amber, and heavier, once there are events behind the reading. The whole
 * reason this is on the card is to stop a confident summary of an order that
 * has since moved being read as current.
 */
.elsa-freshness--stale {
  background: var(--warning-soft);
  color: var(--warning);
  font-weight: 600;
}

.elsa-regen {
  margin-left: auto;
}

/* The verdict: colour on the edge of the words it qualifies. */
.elsa-verdict {
  border-left: 3px solid var(--ink-mute);
  padding-left: 14px;
  margin: 0 0 16px;
}

.elsa-verdict--green {
  border-left-color: var(--success);
}

.elsa-verdict--orange {
  border-left-color: var(--warning);
}

.elsa-verdict--red {
  border-left-color: var(--danger);
}

.elsa-verdict-kicker {
  margin: 0 0 5px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ink-soft);
}

.elsa-verdict--green .elsa-verdict-kicker {
  color: var(--success);
}

.elsa-verdict--orange .elsa-verdict-kicker {
  color: var(--warning);
}

.elsa-verdict--red .elsa-verdict-kicker {
  color: var(--danger);
}

.elsa-verdict-text {
  margin: 0;
  font-size: 16.5px;
  line-height: 1.45;
  color: var(--ink);
  max-width: 62ch;
}

/*
 * The call to action, given a surface of its own.
 *
 * Purple rather than a status colour: this is ELSA asking for something, and
 * painting it amber would make it look like a second verdict.
 */
.elsa-cta {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 0 16px;
  padding: 14px 15px;
  /* AB#21595 — ELSA's own purple, as translucent tints so the "do this next"
     card reads on both the light and the dark ground without a per-theme block. */
  border: 1px solid rgba(124, 92, 214, 0.3);
  border-radius: 9px;
  background: rgba(124, 92, 214, 0.1);
}

.elsa-cta-body {
  flex: 1;
  min-width: 220px;
}

.elsa-cta-kicker {
  margin: 0 0 4px;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #8b6ee0;
}

.elsa-cta-text {
  margin: 0;
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ink);
}

.elsa-cta-why {
  margin: 5px 0 0;
  font-size: 12px;
  color: var(--ink-soft);
}

.elsa-cta-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.elsa-cta-btns .elsa-btn {
  white-space: nowrap;
}

.elsa-verdict-detail {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink-soft);
  max-width: 62ch;
}

.elsa-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  padding: 4px 0;
  border: 0;
  background: none;
  color: var(--accent);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}

.elsa-more:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 4px;
}

.elsa-more-hint {
  color: var(--ink-soft);
  font-weight: 500;
}

.elsa-more-ic {
  width: 16px;
  height: 16px;
  transition: transform 0.15s ease;
}

.elsa-more-ic--open {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .elsa-more-ic {
    transition: none;
  }
}

.elsa-eta {
  margin: 8px 0 0;
  font-size: 13.5px;
  color: var(--ink-soft);
}

.elsa-eta strong {
  color: var(--ink);
  font-weight: 600;
}

.elsa-eta-basis {
  color: var(--ink-soft);
}

.elsa-coverage {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  align-items: center;
}

.elsa-coverage-label {
  font-size: 12px;
  color: var(--ink-soft);
}

.elsa-chip {
  font-size: 11px;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px 9px;
  color: var(--ink-soft);
  background: var(--surface-2);
}

.elsa-chip strong {
  color: var(--ink);
  font-weight: 500;
}

.elsa-btn {
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 5px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.elsa-btn:hover:not(:disabled) {
  background: var(--surface-2);
  border-color: var(--ink-soft);
}

.elsa-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.elsa-btn--primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

.elsa-btn--primary:hover:not(:disabled) {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
}

.elsa-btn--primary .elsa-mark {
  color: #e9ddff;
}

.elsa-empty {
  text-align: center;
  padding: 26px 18px;
}

.elsa-empty p {
  color: var(--ink-soft);
  max-width: 46ch;
  margin: 0 auto 14px;
  font-size: 13.5px;
}

.elsa-notice {
  background: var(--warning-soft);
  border: 1px solid var(--border);
  border-left: 3px solid var(--warning);
  border-radius: 0 8px 8px 0;
  padding: 13px 16px;
  font-size: 13.5px;
  color: var(--ink-soft);
}

.elsa-notice strong {
  color: var(--ink);
}

.elsa-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--danger);
}
.elsa-read-verdict {
  margin-left: auto;
  margin-right: 8px;
}
</style>
